"use client";

import { useEffect } from "react";

/**
 * ChatGPT-Ads-Attribution über den Host-Wechsel www → app (Übergabe
 * 11.09.2026, Abschnitt 6 D): der `__oppref`-Cookie ist hostgebunden. Beim
 * Klick auf einen Link zur Buchungs-App wird der vorhandene Rohwert als
 * `oppref`-Query angehängt; das SDK auf app.isg-express.com liest ihn dort
 * wie einen Landingpage-Parameter. Nur mit Marketing-Einwilligung, keine
 * weiteren Daten, keine Änderung an den Werbe-URLs selbst.
 */
export function withOppref(url: string): string {
  if (typeof document === "undefined") return url;
  try {
    if (!/(?:^|; )cc_cookie=([^;]*)/.test(document.cookie)) return url;
    const cc = JSON.parse(decodeURIComponent(RegExp.$1)) as { categories?: unknown };
    if (!Array.isArray(cc.categories) || !cc.categories.includes("marketing")) return url;
    const m = document.cookie.match(/(?:^|; )__oppref=([^;]*)/);
    const oppref = m ? decodeURIComponent(m[1]).trim() : "";
    if (!oppref || !/^[A-Za-z0-9_.:=-]{1,256}$/.test(oppref)) return url;
    const u = new URL(url, window.location.href);
    if (!/(^|\.)app\.isg-express\.com$/.test(u.hostname) || u.searchParams.has("oppref")) return url;
    u.searchParams.set("oppref", oppref);
    return u.toString();
  } catch {
    return url;
  }
}

/** `ia_src` und Google-Click-IDs (aus URL oder unserem Cookie) an App-Links
 *  anhängen — Kampagnenkennzeichen, keine Personendaten, daher ohne Consent. */
export function withAdsContext(url: string): string {
  if (typeof document === "undefined") return url;
  try {
    const u = new URL(url, window.location.href);
    if (!/(^|\.)app\.isg-express\.com$/.test(u.hostname)) return url;
    const params = new URLSearchParams(window.location.search);
    const m = document.cookie.match(/(?:^|; )isg_ads_ctx=([^;]*)/);
    const stored = m ? (JSON.parse(decodeURIComponent(m[1])) as Record<string, string>) : {};
    for (const k of ["ia_src", "gclid", "gbraid", "wbraid"]) {
      // `iasrc` (Kampagnen-URLs ohne Unterstrich) wird als `ia_src` weitergegeben.
      const v = params.get(k) ?? (k === "ia_src" ? params.get("iasrc") : null) ?? stored[k];
      if (v && /^[A-Za-z0-9_.-]{1,200}$/.test(v) && !u.searchParams.has(k)) u.searchParams.set(k, v);
    }
    return u.toString();
  } catch {
    return url;
  }
}

export function AppLinkAttribution() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || !/app\.isg-express\.com/.test(a.href)) return;
      const next = withOppref(withAdsContext(a.href));
      if (next !== a.href) a.href = next;
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return null;
}
